import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Truck,
  CheckCircle2,
  RefreshCcw,
  Loader2,
  Package,
  Coins,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  Layers,
  AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US");
const dtShort = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ar-IQ", {
      year: "2-digit", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function PendingDriverOrdersTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [openDrivers, setOpenDrivers] = useState({}); // driverId -> bool

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/pending-driver-orders`);
      setData(data);
      // open the first group by default if there's only one
      const groups = data.groups || [];
      if (groups.length) {
        setOpenDrivers((p) => {
          const next = { ...p };
          if (groups.length === 1) next[groups[0].driver_id] = true;
          else groups.forEach((g) => { if (!(g.driver_id in next)) next[g.driver_id] = true; });
          return next;
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل التحميل");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeOne = async (order) => {
    if (!window.confirm(`موافق على إغلاق هذا الطلب وإعطاء السائق ${fmt(order.bonus_due)} د.ع (${order.cartons} كرتون)؟\n\n— لن يتأثر دين الزبون.\n— لن يتأثر صندوق النقد.`)) return;
    setBusyId(order.id);
    try {
      await axios.post(`${API}/admin/pending-driver-orders/${order.id}/close`);
      toast.success(`✅ تم — +${fmt(order.bonus_due)} د.ع للسائق`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل الإغلاق");
    } finally {
      setBusyId(null);
    }
  };

  const closeAllForDriver = async (group) => {
    if (!group.orders.length) return;
    if (!window.confirm(
      `إغلاق جميع طلبات «${group.driver_name}» المعلقة (${group.orders.length} طلب)؟\n\n` +
      `• مجموع الكراتين: ${group.total_cartons}\n` +
      `• المكافأة الإجمالية للسائق: ${fmt(group.total_bonus)} د.ع\n\n` +
      `لن يتأثر أي حساب زبون أو صندوق نقد.`,
    )) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/pending-driver-orders/bulk-close`, {
        order_ids: group.orders.map((o) => o.id),
      });
      const s = data.summary || {};
      toast.success(
        `✅ أُغلق ${s.closed || 0} طلب — مجموع المكافأة ${fmt(s.total_bonus)} د.ع` +
        (s.failed ? ` (فشل ${s.failed})` : ""),
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل الإغلاق الجماعي");
    } finally {
      setBulkBusy(false);
    }
  };

  const closeAll = async () => {
    const all = (data?.groups || []).flatMap((g) => g.orders.map((o) => o.id));
    if (!all.length) return;
    if (!window.confirm(
      `إغلاق ${all.length} طلب من كل السواق دفعة واحدة؟\n\n` +
      `• مجموع الكراتين: ${totals.cartons}\n` +
      `• المكافأة الإجمالية: ${fmt(totals.bonus_due)} د.ع\n\n` +
      `لن يتأثر أي حساب زبون أو صندوق نقد.`,
    )) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/pending-driver-orders/bulk-close`, {
        order_ids: all,
      });
      const s = data.summary || {};
      toast.success(`✅ أُغلق ${s.closed || 0} طلب — مجموع المكافأة ${fmt(s.total_bonus)} د.ع`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل الإغلاق");
    } finally {
      setBulkBusy(false);
    }
  };

  const totals = data?.totals || { drivers: 0, orders: 0, cartons: 0, bonus_due: 0 };
  const groups = data?.groups || [];

  return (
    <div className="space-y-4" dir="rtl" data-testid="pending-driver-orders-tab">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-bl from-[#1e40af] via-[#3B82F6] to-[#1E3A5F] text-white p-5 shadow-2xl">
        <div className="absolute -top-10 -left-10 w-44 h-44 bg-yellow-300/15 rounded-full blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-yellow-200/90 text-xs font-bold tracking-wider uppercase">
              <Truck className="w-4 h-4" />
              إغلاق طلبات السواق المعلقة
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold mt-1">
              طلبات قديمة بانتظار الإغلاق
            </h1>
            <p className="text-white/85 text-xs mt-2 max-w-xl leading-relaxed">
              الطلبات اللي السواق قبلوها ولكن نسوا/تأخروا بزر &quot;تم التوصيل&quot;. اضغط <b>موافق</b> لكل طلب وراح:
            </p>
            <ul className="mt-2 text-white/85 text-[11px] leading-relaxed list-disc pr-4 space-y-0.5">
              <li>✅ يُغلق الطلب فوراً.</li>
              <li>✅ تروح للسائق مكافأته (30 د.ع × عدد الكراتين).</li>
              <li>❌ <b>لا</b> يلمس دين الزبون أو صندوق النقد.</li>
            </ul>
          </div>
          <button
            data-testid="pending-refresh-btn"
            onClick={load}
            className="bg-white/15 hover:bg-white/25 backdrop-blur px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1"
          >
            <RefreshCcw className="w-3.5 h-3.5" />
            تحديث
          </button>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <div className="bg-white rounded-2xl p-3 shadow-sm border border-slate-200">
          <div className="text-[11px] text-slate-500 font-bold">السواق</div>
          <div className="font-extrabold text-2xl text-[#1E3A5F] tabular-nums">{fmt(totals.drivers)}</div>
        </div>
        <div className="bg-white rounded-2xl p-3 shadow-sm border border-blue-200">
          <div className="text-[11px] text-blue-700 font-bold">الطلبات</div>
          <div className="font-extrabold text-2xl text-blue-700 tabular-nums">{fmt(totals.orders)}</div>
        </div>
        <div className="bg-white rounded-2xl p-3 shadow-sm border border-amber-300">
          <div className="text-[11px] text-amber-700 font-bold flex items-center gap-1"><Package className="w-3 h-3" />الكراتين</div>
          <div className="font-extrabold text-2xl text-amber-700 tabular-nums">{fmt(totals.cartons)}</div>
        </div>
        <div className="bg-white rounded-2xl p-3 shadow-sm border border-emerald-300">
          <div className="text-[11px] text-emerald-700 font-bold flex items-center gap-1"><Coins className="w-3 h-3" />مكافأة مستحقة</div>
          <div className="font-extrabold text-2xl text-emerald-700 tabular-nums" dir="ltr">{fmt(totals.bonus_due)} د.ع</div>
        </div>
      </div>

      {/* Master close all */}
      {totals.orders > 1 && (
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-3 flex items-center justify-between gap-2">
          <div className="text-sm text-emerald-900 font-bold flex items-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            إغلاق كل الطلبات دفعة واحدة (آمن — لا يؤثر على حسابات الزبائن)
          </div>
          <button
            data-testid="bulk-close-all-btn"
            onClick={closeAll}
            disabled={bulkBusy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded-xl shadow flex items-center gap-1 disabled:opacity-50"
          >
            {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
            إغلاق الكل ({totals.orders})
          </button>
        </div>
      )}

      {/* Empty state */}
      {loading && !data ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-[#3B82F6]" />
        </div>
      ) : groups.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
          <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-400 mb-3" />
          <p className="text-slate-700 font-bold">ممتاز! لا توجد طلبات معلقة لأي سائق.</p>
          <p className="text-slate-500 text-xs mt-1">كل الطلبات الموكّلة للسواق إما مكتملة أو لم يستلمها سائق بعد.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => {
            const isOpen = !!openDrivers[g.driver_id];
            return (
              <div
                key={g.driver_id}
                data-testid={`driver-group-${g.driver_id}`}
                className="bg-white rounded-2xl shadow-sm border-2 border-blue-200 overflow-hidden"
              >
                {/* Header */}
                <div
                  onClick={() =>
                    setOpenDrivers((p) => ({ ...p, [g.driver_id]: !p[g.driver_id] }))
                  }
                  className="bg-gradient-to-l from-blue-50 to-white px-4 py-3 cursor-pointer flex items-center justify-between gap-3 border-b border-blue-100"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-[#3B82F6] text-white flex items-center justify-center font-bold shrink-0">
                      <Truck className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-sm text-[#1E3A5F] truncate">{g.driver_name}</h3>
                      <div className="text-[11px] text-slate-500" dir="ltr">{g.driver_phone || "—"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="text-center bg-blue-100 text-blue-800 rounded-lg px-2 py-1">
                      <div className="text-[9px] font-bold">طلبات</div>
                      <div className="text-base font-extrabold tabular-nums leading-none">{g.orders.length}</div>
                    </div>
                    <div className="text-center bg-amber-100 text-amber-800 rounded-lg px-2 py-1">
                      <div className="text-[9px] font-bold">كرتون</div>
                      <div className="text-base font-extrabold tabular-nums leading-none">{g.total_cartons}</div>
                    </div>
                    <div className="text-center bg-emerald-100 text-emerald-800 rounded-lg px-2 py-1">
                      <div className="text-[9px] font-bold">د.ع</div>
                      <div className="text-base font-extrabold tabular-nums leading-none" dir="ltr">{fmt(g.total_bonus)}</div>
                    </div>
                    {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                </div>

                {isOpen && (
                  <div className="p-3 space-y-2">
                    {/* Bulk close this driver only */}
                    <button
                      onClick={() => closeAllForDriver(g)}
                      disabled={bulkBusy}
                      data-testid={`close-driver-all-${g.driver_id}`}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold py-2 rounded-xl flex items-center justify-center gap-1 disabled:opacity-50"
                    >
                      {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                      موافقة جماعية على كل طلبات هذا السائق ({g.orders.length})
                    </button>

                    {g.orders.map((o) => (
                      <div
                        key={o.id}
                        data-testid={`pending-order-row-${o.id}`}
                        className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 flex items-center gap-2"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold text-slate-800 truncate">
                            {o.customer_name}
                            {o.customer_ipad && (
                              <span className="bg-[#3B82F6]/15 text-[#1E3A5F] text-[9px] font-bold rounded-full px-1.5 py-0.5 mr-1">ايباد {o.customer_ipad}</span>
                            )}
                          </div>
                          <div className="text-[10px] text-slate-500 flex items-center gap-2 mt-0.5">
                            <span>{dtShort(o.created_at)}</span>
                            <span className="bg-slate-200 text-slate-700 rounded-full px-1.5">{o.status}</span>
                          </div>
                        </div>
                        <div className="text-center flex-shrink-0">
                          <div className="text-[9px] text-slate-500 font-bold">كرتون</div>
                          <div className="text-sm font-extrabold text-amber-700 tabular-nums">{o.cartons}</div>
                        </div>
                        <div className="text-center flex-shrink-0">
                          <div className="text-[9px] text-slate-500 font-bold">مكافأة</div>
                          <div className="text-sm font-extrabold text-emerald-700 tabular-nums" dir="ltr">{fmt(o.bonus_due)}</div>
                        </div>
                        <button
                          onClick={() => closeOne(o)}
                          disabled={busyId === o.id}
                          data-testid={`close-order-${o.id}`}
                          className="bg-gradient-to-l from-[#7ED321] to-[#5fa617] text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow flex items-center gap-1 disabled:opacity-50 flex-shrink-0"
                        >
                          {busyId === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                          موافق
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer reassurance */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-3 text-[11px] text-blue-900 leading-relaxed flex items-start gap-2">
        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          هذه الشاشة تخص الطلبات القديمة فقط. الطلبات الجديدة يقفلها السائق بنفسه بالضغط على زر &quot;تم التوصيل&quot; بصفحته. لا تظهر هذه الشاشة إذا لم تكن هناك طلبات معلقة.
        </div>
      </div>
    </div>
  );
}
